import { ErrorableLoader } from '@bordrio/ui-components';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSelectInput } from '@src/components';
import { useGetDocumentsQuery } from '@src/generated/graphql';
import { useRequiredUserId } from '@src/hooks/useRequiredUserId';
import { DocumentsTable } from './components/DocumentsTable/DocumentsTable';
import { usePrepareDocumentEntries } from './hooks/usePrepareDocumentEntries';

export function DocumentsPage(): JSX.Element {
  const userId = useRequiredUserId();
  const { data, error } = useGetDocumentsQuery({
    variables: { userId },
  });

  const [recipientFilter, setRecipientFilter] = useState<string>('');

  const { t } = useTranslation();
  const prepareDocumentEntries = usePrepareDocumentEntries();

  if (!data) {
    return (
      <div className="flex flex-1 items-center justify-center pt-8">
        <ErrorableLoader error={error} />
      </div>
    );
  }

  const allDocuments = prepareDocumentEntries(
    data.fiscalRepDocuments,
    data.nifOrders,
  );

  const recipients = [
    { label: t('documents.recipient_filter.all', 'All recipients'), value: '' },
    ..._.sortBy(
      _.uniqBy(
        allDocuments.map((d) => ({
          label: d.recipient,
          value: d.recipient,
        })),
        (s) => s.value,
      ),
      (s) => s.label,
    ),
  ];

  const documents = _.sortBy(
    allDocuments.filter((d) => {
      if (!recipientFilter) return true;
      return d.recipient === recipientFilter;
    }),
    (d) => d.createdAt,
  ).reverse();

  return (
    <div className="flex-1 space-y-4 sm:space-y-8">
      <header>
        <div className="app-layout-wrapper">
          <h1>{t('documents.title', 'Documents')}</h1>
        </div>
      </header>
      <main>
        <div className="app-layout-wrapper space-y-8">
          {allDocuments.length === 0 ? (
            <p>
              {t(
                'documents.empty_state.description',
                "You don't have any documents to view at this time.",
              )}
            </p>
          ) : (
            <>
              <ReactSelectInput.Labelled
                label="Recipient"
                options={recipients}
                value={recipientFilter}
                onChange={(value) => setRecipientFilter(value ?? '')}
                className="max-w-sm"
              />
              <DocumentsTable documents={documents} />
            </>
          )}
        </div>
      </main>
    </div>
  );
}
